import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { 
  getSocialBanners,
  toggleLike,
  submitComment,
  deleteComment,
  likeComment,
  formatCommentForDisplay,
  getLikes,
  getComments,
} from '../../services/socialBannerApi';
import { usePromoSocial } from './usePromoSocial';
import SocialBannerCard from './SocialBannerCard';
import PromoLikeSheet from './PromoLikeSheet';
import PromoCommentSheet from './PromoCommentSheet';
import PromoShareSheet from './PromoShareSheet';
import { useWebSocket } from '../websocket/useWebSocket';
import Constants from "expo-constants";

export default function SocialBannerFeed({
  isAuthenticated = true,
  onNeedLogin,
  currentUser,
  openBannerId,
  openBannerNonce,
  onOpenedBanner,
  onClosedBanner,
}) {
  const [items, setItems] = useState([]);
  const social = usePromoSocial({ isAuthenticated, onNeedLogin, currentUser });
  const openedKeyRef = useRef(null);
  const notifyCloseFromDeepLinkRef = useRef(false);
  const { 
    isConnected: wsIsConnected, 
    messages: wsMessages, 
    trigger: wstrigger, 
    bind: wsBind
  } = useWebSocket(Constants.expoConfig?.extra?.WEBSOCKET_CHANNEL);

  // ws event
  useEffect(() => {
    wsBind('social-banner', (data) => {
      if( data && 'id_banner' in data && 'comment_displayed' in data ) {
        const entry = social.getEntry(data?.id_banner);
        if( entry ) {
          social.updateCommentDisplayState(data.id_banner, data.comment_displayed);
        }
      }
    });

    wsBind('social-banner-like', (data) => {
      if( data && 'id_banner' in data && 'total_like' in data ) {
        const entry = social.getEntry(data?.id_banner);
        if( entry ) {
          social.updateLikeCount(data.id_banner, data.total_like);
        }
      }
    });

    wsBind('social-banner-comment', (data) => {
      if( data && 'id_banner' in data && 'comments' in data ) {
        const entry = social.getEntry(data?.id_banner);
        if( entry ) {
          data.comments.forEach(c => {
            if(data.hide) {
              social.deleteComment(data.id_banner, c.id);
            } else {
              const comment = formatCommentForDisplay(c);
              social.addComment(
                data.id_banner, 
                comment.parentId, 
                comment.id, 
                comment.userId, 
                comment.user, 
                comment.avatar, 
                comment.text, 
                comment.likers, 
                comment.timestamp, 
                comment.mention || '',
              );
            }
          });
          social.updateCommentCount(data.id_banner, data.total_comment);
        }
      }
    });

    wsBind('social-banner-share', (data) => {
      if( data && 'id_banner' in data && 'total_share' in data ) {
        const entry = social.getEntry(data?.id_banner);
        if( entry ) {
          social.updateShareCount(data.id_banner, data.total_share);
        }
      }
    });

    wsBind('social-banner-view', (data) => {
      if( data && 'id_banner' in data && 'total_view' in data ) {
        const entry = social.getEntry(data?.id_banner);
        if( entry ) {
          social.updateViewCount(data.id_banner, data.total_view);
        }
      }
    });

    wsBind('social-banner-comment-like', (data) => {
      if( data && 'id_banner' in data && 'id_comment' in data && 'likers' in data ) {
        const entry = social.getEntry(data?.id_banner);
        if( entry ) {
          social.updateCommentLikers(data.id_banner, data.id_comment, data.likers);
        }
      }
    });
  }, [wsBind]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await getSocialBanners();
      if (cancelled) return;
      const list = response || [];
      setItems(list);
      list.forEach((item) => social.hydrate(item));
    })();
    return () => {
      cancelled = true;
    };
  }, [social.hydrate]);

  useEffect(() => {
    if (!openBannerId || !items.length) return;
    const item = items.find((entry) => String(entry.id) === String(openBannerId));
    const openKey = `${openBannerId}:${openBannerNonce || ''}`;
    if (openedKeyRef.current === openKey) return;
    openedKeyRef.current = openKey;
    if (!item) {
      if (onOpenedBanner) onOpenedBanner();
      if (onClosedBanner) onClosedBanner();
      return;
    }
    notifyCloseFromDeepLinkRef.current = true;
    handleOpenComments(item);
    if (onOpenedBanner) onOpenedBanner();
  }, [items, openBannerId, openBannerNonce, onOpenedBanner, onClosedBanner, social.openComments]);

  const handleOpenComments = async (item) => {
    if (social.guardAuth()) {
      const result = await getComments(item.id);
      social.setComments(item.id, result);
      social.openComments(item);
    }
  }

  const handleCloseComments = () => {
    const wasOpen = social.commentSheet.visible;
    const fromDeepLink = notifyCloseFromDeepLinkRef.current;
    social.closeComments();
    if (wasOpen && fromDeepLink) {
      notifyCloseFromDeepLinkRef.current = false;
      if (onClosedBanner) onClosedBanner();
    }
  };

  const handleSubmitComment = async (itemId, parentId, text) => {
    const result = await submitComment(itemId, parentId, text);
    let hidden = false;
    const success = !!result;
    const comment = success ? result.comment : null;

    if (success) {
      const entry = social.getEntry(itemId);
      if (entry) {
        social.updateCommentCount(itemId, result.commentCount);
        hidden = !entry.commentDisplayed;
        if (entry.commentDisplayed) {
          social.addComment(
            itemId, 
            parentId,
            comment.id, 
            comment.userId, 
            comment.user, 
            comment.avatar, 
            comment.text, 
            comment.likers,
            comment.timestamp
          );
        }
      }
    }

    return {success, hidden, comment};
  }

  const handleDeleteComment = async (itemId, commentId) => {
    const result = await deleteComment(commentId);
    if (!!result) {
      social.deleteComment(itemId, commentId);
      social.updateCommentCount(itemId, result.commentCount);
    }
  }

  const handleLikeComment = async (itemId, commentId) => {
    const result = await likeComment(commentId);
    if (!!result) {
      social.updateCommentLikers(itemId, commentId, result.likers);
    }
  }

  const handleOpenLikes = async (item) => {
    const result = await getLikes(item.id);
    social.openLikes(item, result);
  }

  const handleCloseLikes = () => {
    social.closeLikes();
  };

  const handleLike = async (item) => {
    if (social.guardAuth()) {
      const result = await toggleLike(item.id);
      social.updateLikeState(item.id, result ? result.liked : entry.liked);
      social.updateLikeCount(item.id, result ? result.likeCount : entry.likeCount);
    }
  }

  return (
    <View style={{ gap: 16, marginBottom: 4 }}>
      {items.map((item) => {
        const entry = social.getEntry(item.id);
        return (
          <SocialBannerCard
            key={item.id}
            item={item}
            liked={entry.liked}
            viewCount={entry.viewCount}
            likeCount={entry.likeCount}
            commentCount={entry.commentCount}
            shareCount={entry.shareCount}
            onLike={() => handleLike(item)}
            onOpenLike={() => handleOpenLikes(item)}
            onComment={() => handleOpenComments(item)}
            onShare={() => social.openShare(item)}
            onOpenDetail={() => handleOpenComments(item)}
          />
        );
      })}

      <PromoLikeSheet
        visible={social.likeSheet.visible}
        likes={social.likeSheet.likes}
        onClose={handleCloseLikes}
      />

      <PromoCommentSheet
        visible={social.commentSheet.visible}
        item={social.commentSheet.item}
        comments={
          social.commentSheet.item ? social.getEntry(social.commentSheet.item.id).comments : []
        }
        currentUser={social.currentUser}
        onClose={handleCloseComments}
        onSubmit={async (itemId, parentId, text) => await handleSubmitComment(itemId, parentId, text)}
        onDelete={async (itemId, commentId) => await handleDeleteComment(itemId, commentId)}
        onLike={async (itemId, commentId) => await handleLikeComment(itemId, commentId)}
      />

      <PromoShareSheet
        visible={social.shareSheet.visible}
        item={social.shareSheet.item}
        currentUser={social.currentUser}
        onClose={social.closeShare}
      />
    </View>
  );
}
